import { attributes, type HtmlValue, html, joinClasses } from 'BomdisoftComponents/html/index';
import { CHEVRON_RIGHT_GLYPH, OPEN_IN_NEW_ROUNDED_GLYPH } from 'BomdisoftComponents/icons/glyphs';
import { renderIcon } from 'BomdisoftComponents/icons/render';

export const MENU_TAG = 'bomdisoft-menu';

export type MenuRole = 'dialog' | 'listbox' | 'menu';

export type MenuOptions = {
  align?: 'center' | 'start';
  anchorLeft?: number;
  anchorTop?: number;
  children?: HtmlValue;
  className?: string;
  id: string;
  label?: string;
  matchAnchorWidth?: boolean;
  open?: boolean;
  placement?: 'below' | 'side';
  role?: MenuRole;
  testId?: string;
};

export type MenuItemOptions = {
  annotation?: string;
  annotationAction?: string;
  children?: HtmlValue;
  className?: string;
  containerRole?: MenuRole;
  disabled?: boolean;
  external?: boolean;
  href?: string;
  id?: string;
  keepOpenOnClick?: boolean;
  selected?: boolean;
  testId?: string;
  variant?: 'action' | 'default' | 'danger';
};

export type MenuSubmenuOptions = {
  children?: HtmlValue;
  disabled?: boolean;
  id: string;
  label: HtmlValue;
  testId?: string;
  value?: HtmlValue;
};

export const renderMenu = ({ align, anchorLeft, anchorTop, children, className, id, label, matchAnchorWidth, open, placement, role = 'menu', testId }: MenuOptions) =>
  html`<bomdisoft-menu${attributes({
    align: align === 'start' ? 'start' : undefined,
    'anchor-left': anchorLeft,
    'anchor-top': anchorTop,
    'aria-label': label,
    class: className,
    'data-testid': testId,
    id,
    'match-anchor-width': matchAnchorWidth,
    open,
    placement: placement === 'side' ? 'side' : undefined,
    popover: 'auto',
    role,
  })}>${children}</bomdisoft-menu>`;

export const renderMenuItem = ({ annotation, annotationAction, children, className, containerRole = 'menu', disabled, external, href, id, keepOpenOnClick, selected, testId, variant = 'default' }: MenuItemOptions) => {
  const shared = { class: joinClasses('bomdisoft-menu-item', className), 'data-keep-open': keepOpenOnClick };
  const tagged = { 'data-prodantix': annotation, 'data-prodantix-action': annotationAction, 'data-testid': testId, 'data-variant': variant === 'default' ? undefined : variant };
  if (href) {
    const glyph = external ? html`<span aria-hidden="true" class="bomdisoft-menu-external">${renderIcon(OPEN_IN_NEW_ROUNDED_GLYPH, { size: 16 })}</span>` : '';
    return html`<a${attributes({ ...shared, ...tagged, href, id, rel: external ? 'noopener noreferrer' : undefined, role: 'menuitem', target: external ? '_blank' : undefined })}>${children}${glyph}</a>`;
  }
  if (containerRole === 'listbox') return html`<button${attributes({ 'aria-selected': selected ? 'true' : 'false', ...shared, 'data-selected': selected, ...tagged, disabled, id, role: 'option', type: 'button' })}>${children}</button>`;
  return html`<button${attributes({ ...shared, 'data-selected': selected, ...tagged, disabled, id, role: containerRole === 'dialog' ? undefined : 'menuitem', type: 'button' })}>${children}</button>`;
};

export const renderMenuDivider = () => html`<hr class="bomdisoft-menu-divider">`;

export const renderMenuSubmenu = ({ children, disabled, id, label, testId, value }: MenuSubmenuOptions) =>
  html`<button${attributes({ 'aria-expanded': 'false', 'aria-haspopup': 'menu', class: 'bomdisoft-menu-item', 'data-submenu': true, 'data-testid': testId, disabled, popovertarget: id, popovertargetaction: 'show', role: 'menuitem', type: 'button' })}><span class="bomdisoft-menu-submenu-label">${label}</span>${value === undefined ? '' : html`<span class="bomdisoft-menu-submenu-value">${value}</span>`}<span aria-hidden="true" class="bomdisoft-menu-submenu-chevron">${renderIcon(CHEVRON_RIGHT_GLYPH, { size: 16 })}</span></button>${renderMenu({ children, className: 'bomdisoft-menu-submenu', id, placement: 'side' })}`;
